import type { DisplayItem } from '../services/quests-service';

const STORAGE_KEY = 'chatQuestTests';
const MAX_TESTS_PER_QUEST = 3;
const MAX_AGE_HOURS = 24;

export interface SavedTestRun {
  id: string;
  questTemplateId: string;
  timestamp: number;
  extraGuidance: string;
  maxTurns: number;
  questType: 'chat' | 'check-in';
  conversation: DisplayItem[];
  totalTurns: number;
  state: 'success' | 'error' | 'stopped';
  error?: string;
}

interface TestStorage {
  [questTemplateId: string]: SavedTestRun[];
}

/**
 * Get all saved tests from local storage
 */
const getAllTests = (): TestStorage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading test storage:', error);
    return {};
  }
};

/**
 * Save all tests to local storage
 */
const saveAllTests = (tests: TestStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tests));
  } catch (error) {
    console.error('Error saving test storage:', error);
  }
};

/**
 * Clean up old tests (older than 24 hours) and keep only the latest 3 tests per quest
 */
export const cleanupOldTests = (): void => {
  const allTests = getAllTests();
  const now = Date.now();
  const maxAge = MAX_AGE_HOURS * 60 * 60 * 1000; // 24 hours in milliseconds

  const cleanedTests: TestStorage = {};

  Object.keys(allTests).forEach((questTemplateId) => {
    const questTests = allTests[questTemplateId];

    // Filter out tests older than 24 hours
    const recentTests = questTests.filter((test) => {
      const age = now - test.timestamp;
      return age < maxAge;
    });

    // Sort by timestamp (newest first) and keep only the latest 3
    const sortedTests = recentTests.sort((a, b) => b.timestamp - a.timestamp);
    const latestTests = sortedTests.slice(0, MAX_TESTS_PER_QUEST);

    // Only keep the quest if it has tests
    if (latestTests.length > 0) {
      cleanedTests[questTemplateId] = latestTests;
    }
  });

  saveAllTests(cleanedTests);
};

/**
 * Save a test run to local storage
 */
export const saveTestRun = (testRun: Omit<SavedTestRun, 'id' | 'timestamp'>): string => {
  const allTests = getAllTests();

  // Generate a unique ID for this test run
  const id = `test_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const timestamp = Date.now();

  const newTestRun: SavedTestRun = {
    ...testRun,
    id,
    timestamp,
  };

  // Get existing tests for this quest or initialize empty array
  const questTests = allTests[testRun.questTemplateId] || [];

  // Add the new test run
  questTests.unshift(newTestRun); // Add to the beginning (newest first)

  // Keep only the latest MAX_TESTS_PER_QUEST tests
  const updatedQuestTests = questTests.slice(0, MAX_TESTS_PER_QUEST);

  // Update the storage
  allTests[testRun.questTemplateId] = updatedQuestTests;
  saveAllTests(allTests);

  return id;
};

/**
 * Get saved test runs for a specific quest
 */
export const getTestRunsForQuest = (questTemplateId: string): SavedTestRun[] => {
  const allTests = getAllTests();
  return allTests[questTemplateId] || [];
};

/**
 * Get a specific test run by ID
 */
export const getTestRunById = (questTemplateId: string, testId: string): SavedTestRun | null => {
  const questTests = getTestRunsForQuest(questTemplateId);
  return questTests.find((test) => test.id === testId) || null;
};

/**
 * Format a timestamp for display
 */
export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
};
